import { RouteComponentProps } from "@reach/router"
import { ApiError } from "@shared/primitive-components"
import { createContext, PropsWithChildren, useContext, useMemo } from "react"
import { InsightsPeriod } from "../api/insights"
import { GetPeriodsOutput } from "../api/insights/periods/utils"
import { FleetEntryWithMileage } from "../api/rebates/useGetFleetEntriesWithMileage"
import { useFleetEntriesWithMileage } from "./hooks"
import { useInsightPeriods } from "./hooks/useInsightPeriods"

export type InsightsContextType = {
  periods?: GetPeriodsOutput
  fleetEntriesWithMileage?: ReadonlyArray<FleetEntryWithMileage>
  insightsProviderLoading?: boolean
  fleetEntriesProviderLoading?: boolean
  v2AccumulatedMileage?: number | null
}

export const InsightsContext = createContext<InsightsContextType | undefined>(undefined)

type ProtectedInsightsProviderProps = {
  periods?: GetPeriodsOutput
  fleetEntriesWithMileage?: ReadonlyArray<FleetEntryWithMileage>
  insightsProviderLoading?: boolean
  fleetEntriesProviderLoading?: boolean
  v2AccumulatedMileage?: number | null
}

const ProtectedInsightsProvider = ({
  periods,
  fleetEntriesWithMileage,
  insightsProviderLoading,
  fleetEntriesProviderLoading,
  v2AccumulatedMileage,
  children,
}: PropsWithChildren<ProtectedInsightsProviderProps>) => {
  const value = useMemo<InsightsContextType>(
    () => ({
      periods,
      fleetEntriesWithMileage,
      insightsProviderLoading,
      fleetEntriesProviderLoading,
      v2AccumulatedMileage,
    }),
    [periods, fleetEntriesWithMileage, insightsProviderLoading, fleetEntriesProviderLoading, v2AccumulatedMileage]
  )
  return <InsightsContext.Provider value={value}>{children}</InsightsContext.Provider>
}

ProtectedInsightsProvider.defaultProps = {
  periods: undefined,
  fleetEntriesWithMileage: undefined,
  insightsProviderLoading: undefined,
  fleetEntriesProviderLoading: undefined,
  v2AccumulatedMileage: undefined,
}

export const InsightsProvider = ({ children }: PropsWithChildren<RouteComponentProps>) => {
  const { apiErrored: errInsights, data: periods, loading: insightsProviderLoading } = useInsightPeriods()
  const {
    apiErrored: errEntries,
    data: fleetEntriesWithMileage,
    loading: fleetEntriesProviderLoading,
  } = useFleetEntriesWithMileage()

  const v2AccumulatedMileage =
    periods?.periods?.reduce(
      (mileage: number, period: InsightsPeriod) =>
        mileage + (typeof period?.analytics?.distance === `number` ? period.analytics.distance : 0),
      0
    ) ?? null

  if (errInsights || errEntries) return <ApiError />
  return (
    <ProtectedInsightsProvider
      {...{
        periods,
        fleetEntriesWithMileage,
        insightsProviderLoading,
        fleetEntriesProviderLoading,
        v2AccumulatedMileage,
      }}
    >
      {children}
    </ProtectedInsightsProvider>
  )
}

export const useInsights = () => {
  const context = useContext(InsightsContext)
  if (context === undefined) {
    throw new Error(`useInsights must be used inside InsightsProvider.`)
  }
  return context
}
